import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import VideoPlayer from '../components/VideoPlayer';
import SimpleMarkdown from '../components/SimpleMarkdown';
import CommentsSection from '../components/CommentsSection';
import ShareModal from '../components/ShareModal';
import WebinarStatsModal from '../components/WebinarStatsModal';
import QuizStatsModal from '../components/QuizStatsModal';
import { FiShare2, FiInfo, FiX, FiThumbsDown, FiBarChart2, FiFileText, FiChevronDown, FiChevronUp, FiEdit, FiTrash2, FiPlus, FiArrowLeft, FiEye, FiHeart, FiUpload, FiCpu, FiCheckSquare } from 'react-icons/fi';
import { isPeertubeInProgress, peertubeStatusLabel, isAiBriefInProgress, aiBriefStatusLabel } from '../utils/peertubeStatus';
import './WebinarDetail.css';

const WebinarDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isModeratorOrManager, isModerator, canShare, isClient } = useAuth();
  const [webinar, setWebinar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showQuizStatsModal, setShowQuizStatsModal] = useState(false);
  const [transcriptionStatus, setTranscriptionStatus] = useState(null);
  const [showCancelButton, setShowCancelButton] = useState(false);
  const transcriptionStatusIntervalRef = useRef(null);
  const transcriptionTaskIdRef = useRef(null);
  const peertubeStatusIntervalRef = useRef(null);
  const videoPlayerRef = useRef(null);
  const [transcriptionExpanded, setTranscriptionExpanded] = useState(false);
  const [editingTags, setEditingTags] = useState(false);
  const [tagsDraft, setTagsDraft] = useState([]);
  const [newTagValue, setNewTagValue] = useState('');
  const [savingTags, setSavingTags] = useState(false);
  const [initialProgressApplied, setInitialProgressApplied] = useState(false);
  const progressSaveTimeoutRef = useRef(null);
  const [currentPositionSeconds, setCurrentPositionSeconds] = useState(0);
  const [likeLoading, setLikeLoading] = useState(false);
  const [savedProgressSeconds, setSavedProgressSeconds] = useState(0);

  useEffect(() => {
    fetchWebinar();
    // Статус транскрипции доступен только модератору.
    if (isModerator) {
      checkTranscriptionStatus();
    }
    
    return () => {
      // Очищаем интервал при размонтировании компонента
      if (transcriptionStatusIntervalRef.current) {
        clearInterval(transcriptionStatusIntervalRef.current);
      }
      if (peertubeStatusIntervalRef.current) {
        clearInterval(peertubeStatusIntervalRef.current);
      }
    };
  }, [id, isModerator]);

  useEffect(() => {
    if (!webinar || !(isPeertubeInProgress(webinar.peertube_status) || isAiBriefInProgress(webinar.ai_brief_status))) {
      if (peertubeStatusIntervalRef.current) {
        clearInterval(peertubeStatusIntervalRef.current);
        peertubeStatusIntervalRef.current = null;
      }
      return undefined;
    }
    peertubeStatusIntervalRef.current = setInterval(async () => {
      try {
        const response = await axios.get(`/api/webinars/${id}/`);
        setWebinar(response.data);
      } catch (error) {
        // ignore polling errors
      }
    }, 5000);
    return () => {
      if (peertubeStatusIntervalRef.current) {
        clearInterval(peertubeStatusIntervalRef.current);
        peertubeStatusIntervalRef.current = null;
      }
    };
  }, [id, webinar?.peertube_status, webinar?.ai_brief_status]);

  useEffect(() => {
    if (!webinar || !videoPlayerRef.current || initialProgressApplied === true) return;
    applySavedProgress();
  }, [webinar, initialProgressApplied]);

  useEffect(() => {
    const interval = setInterval(() => {
      saveViewProgress(false);
    }, 15000);

    const onBeforeUnload = () => {
      saveViewProgress(false);
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', onBeforeUnload);
      saveViewProgress(false);
    };
  }, [webinar]);

  // Автоматически начинаем с первой временной метки, если нет сохраненного прогресса.
  useEffect(() => {
    if (savedProgressSeconds > 0) return;
    if (!webinar || !webinar.timestamps || webinar.timestamps.length === 0) return;
    if (!videoPlayerRef.current) return;
    const firstTimestamp = webinar.timestamps[0];
    if (!firstTimestamp || typeof firstTimestamp.time_seconds !== 'number') return;

    const timeoutId = setTimeout(() => {
      if (!videoPlayerRef.current) return;
      try {
        videoPlayerRef.current.seekTo(firstTimestamp.time_seconds);
      } catch (e) {
        console.error('Error auto-seeking to first timestamp:', e);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [webinar, savedProgressSeconds]);

  const fetchWebinar = async () => {
    try {
      const response = await axios.get(`/api/webinars/${id}/`);
      setWebinar(response.data);
      setInitialProgressApplied(false);
    } catch (error) {
      toast.error('Ошибка загрузки вебинара');
      navigate('/webinars');
    } finally {
      setLoading(false);
    }
  };

  const applySavedProgress = async () => {
    try {
      const response = await axios.get(`/api/webinars/${id}/view-progress/`);
      const lastPosition = parseInt(response.data?.last_position_seconds || 0, 10);
      if (lastPosition > 0) {
        setSavedProgressSeconds(lastPosition);
        setCurrentPositionSeconds(lastPosition);
        setTimeout(() => {
          try {
            if (videoPlayerRef.current) {
              videoPlayerRef.current.seekTo(lastPosition);
            }
          } catch (error) {
            console.error('Error applying saved progress:', error);
          }
        }, 500);
      }
    } catch (error) {
      // no-op
    } finally {
      setInitialProgressApplied(true);
    }
  };

  const saveViewProgress = async (isCompleted = false, positionOverride = null) => {
    if (!videoPlayerRef.current || !webinar) return;
    const current = parseInt(
      positionOverride !== null && positionOverride !== undefined
        ? positionOverride
        : (videoPlayerRef.current.getCurrentTime?.() || currentPositionSeconds || 0),
      10
    );
    try {
      await axios.post(`/api/webinars/${id}/view-progress/`, {
        position_seconds: current,
        max_position_seconds: current,
        is_completed: isCompleted,
      });
    } catch (error) {
      // no-op
    }
  };

  const handleVideoProgress = (seconds) => {
    if (!webinar) return;
    setCurrentPositionSeconds(parseInt(seconds || 0, 10));
    if (progressSaveTimeoutRef.current) {
      clearTimeout(progressSaveTimeoutRef.current);
    }
    progressSaveTimeoutRef.current = setTimeout(() => {
      saveViewProgress(false, seconds);
    }, 2500);
  };

  const handleToggleLike = async () => {
    if (likeLoading || !webinar) return;
    setLikeLoading(true);
    try {
      const response = await axios.post(`/api/webinars/${id}/like/`);
      setWebinar((prev) => ({
        ...prev,
        likes_count: response.data?.likes_count ?? prev.likes_count,
        dislikes_count: response.data?.dislikes_count ?? prev.dislikes_count,
      }));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка лайка');
    } finally {
      setLikeLoading(false);
    }
  };

  const checkTranscriptionStatus = async () => {
    if (!isModerator) {
      return;
    }
    try {
      const taskId = transcriptionTaskIdRef.current;
      const url = taskId
        ? `/api/webinars/${id}/transcription-status/?task_id=${encodeURIComponent(taskId)}`
        : `/api/webinars/${id}/transcription-status/`;
      const response = await axios.get(url);
      
      if (response.data.state && response.data.state !== 'no_task') {
        setTranscriptionStatus(response.data);
        
        // Если задача в процессе, запускаем polling
        if (response.data.state === 'PROGRESS' || response.data.state === 'PENDING') {
          if (!transcriptionStatusIntervalRef.current) {
            transcriptionStatusIntervalRef.current = setInterval(() => {
              checkTranscriptionStatus();
            }, 2000); // Проверяем каждые 2 секунды
          }
        } else {
          // Задача завершена или ошибка - останавливаем polling
          if (transcriptionStatusIntervalRef.current) {
            clearInterval(transcriptionStatusIntervalRef.current);
            transcriptionStatusIntervalRef.current = null;
          }
          
          if (response.data.state === 'SUCCESS') {
            toast.success('Транскрипция успешно создана и отправлена на email');
            fetchWebinar(); // Обновляем данные вебинара
            transcriptionTaskIdRef.current = null;
          } else if (response.data.state === 'FAILURE') {
            toast.error(`Ошибка генерации транскрипции: ${response.data.error || 'Неизвестная ошибка'}`);
            transcriptionTaskIdRef.current = null;
          }
          
          // Очищаем статус через 5 секунд
          setTimeout(() => {
            setTranscriptionStatus(null);
          }, 5000);
        }
      } else {
        setTranscriptionStatus(null);
        if (transcriptionStatusIntervalRef.current) {
          clearInterval(transcriptionStatusIntervalRef.current);
          transcriptionStatusIntervalRef.current = null;
        }
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        if (transcriptionStatusIntervalRef.current) {
          clearInterval(transcriptionStatusIntervalRef.current);
          transcriptionStatusIntervalRef.current = null;
        }
        setTranscriptionStatus(null);
        return;
      }
      // Игнорируем ошибки проверки статуса (задача может не существовать)
      if (error.response?.status !== 404) {
        console.error('Ошибка проверки статуса транскрипции:', error);
      }
    }
  };

  const handleGenerateTranscription = async () => {
    try {
      const response = await axios.post(`/api/webinars/${id}/generate-transcription/`);
      
      // Если есть предупреждение о существующей транскрипции
      if (response.data.warning && response.data.has_existing_transcription) {
        const confirmMessage = `${response.data.message}\n\nДлина существующей транскрипции: ${response.data.existing_length} символов`;
        const shouldRegenerate = window.confirm(confirmMessage);
        
        if (shouldRegenerate) {
          // Отправляем запрос с подтверждением перезаписи
          const regenerateResponse = await axios.post(`/api/webinars/${id}/generate-transcription/`, {
            force_regenerate: true
          });
          if (regenerateResponse.data.task_id) {
            transcriptionTaskIdRef.current = regenerateResponse.data.task_id;
          }
          toast.success(regenerateResponse.data.message || 'Генерация новой транскрипции запущена.');
          checkTranscriptionStatus();
        } else {
          toast.info('Генерация транскрипции отменена');
        }
      } else {
        // Обычный случай - транскрипции нет
        if (response.data.task_id) {
          transcriptionTaskIdRef.current = response.data.task_id;
        }
        toast.success(response.data.message || 'Генерация транскрипции запущена.');
        checkTranscriptionStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка запуска генерации транскрипции');
    }
  };

  const handleCancelTranscription = async () => {
    if (!window.confirm('Вы уверены, что хотите отменить генерацию транскрипции?')) {
      return;
    }
    
    try {
      await axios.post(`/api/webinars/${id}/cancel-transcription/`);
      toast.info('Генерация транскрипции отменена');
      setTranscriptionStatus(null);
      if (transcriptionStatusIntervalRef.current) {
        clearInterval(transcriptionStatusIntervalRef.current);
        transcriptionStatusIntervalRef.current = null;
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка отмены генерации транскрипции');
    }
  };

  const startEditTags = () => {
    setEditingTags(true);
    setTagsDraft(Array.isArray(webinar?.ai_tags) ? [...webinar.ai_tags] : []);
    setNewTagValue('');
  };

  const cancelEditTags = () => {
    setEditingTags(false);
    setTagsDraft([]);
    setNewTagValue('');
  };

  const handleAddTag = () => {
    let value = (newTagValue || '').trim();
    if (!value) return;
    if (!value.startsWith('#')) {
      value = `#${value}`;
    }
    if (tagsDraft.includes(value)) {
      setNewTagValue('');
      return;
    }
    setTagsDraft([...tagsDraft, value]);
    setNewTagValue('');
  };

  const handleDeleteTag = (index) => {
    setTagsDraft(tagsDraft.filter((_, i) => i !== index));
  };

  const handleSaveTags = async () => {
    if (!webinar) return;
    setSavingTags(true);
    try {
      const payload = { ai_tags: tagsDraft };
      await axios.patch(`/api/webinars/${id}/`, payload);
      setWebinar({ ...webinar, ai_tags: tagsDraft });
      toast.success('Теги обновлены');
      setEditingTags(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка сохранения тегов');
    } finally {
      setSavingTags(false);
    }
  };

  const handleRetryPeertubeUpload = async () => {
    try {
      const response = await axios.post(`/api/webinars/${id}/upload_to_peertube/`);
      setWebinar({
        ...webinar,
        peertube_status: response.data.peertube_status || 'queued',
        peertube_error: response.data.peertube_error || '',
        external_peertube_url: response.data.external_peertube_url || webinar.external_peertube_url,
      });
      toast.success('Загрузка в PeerTube поставлена в очередь');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось отправить видео в PeerTube');
    }
  };

  const handleGenerateAiBrief = async () => {
    try {
      const response = await axios.post(`/api/webinars/${id}/generate_ai_brief/`);
      setWebinar({
        ...webinar,
        ai_brief_status: response.data.ai_brief_status || 'waiting_captions',
        ai_brief_error: response.data.ai_brief_error || '',
      });
      toast.success('Берём субтитры PeerTube и заполняем карточку через ИИ');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось запустить автозаполнение');
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  if (!webinar) {
    return <div className="empty-state">Вебинар не найден</div>;
  }

  const hasChronology = !!(webinar.chronology_structure && webinar.chronology_structure.trim());
  const canDistributeWebinar = !webinar.employee_only_distribution || isModerator;
  const chronologyLines = hasChronology
    ? webinar.chronology_structure.split('\n').map((l) => l.trim()).filter(Boolean)
    : [];

  const parseHms = (hms) => {
    const p = String(hms).split(':').map((v) => parseInt(v, 10));
    if (p.length !== 3 || p.some((n) => Number.isNaN(n))) return null;
    return p[0] * 3600 + p[1] * 60 + p[2];
  };

  const seekToSeconds = (seconds) => {
    if (!videoPlayerRef.current) return;
    try {
      videoPlayerRef.current.seekTo(seconds);
    } catch (error) {
      console.error('Error seeking from chronology:', error);
    }
  };

  return (
    <div className="webinar-detail">
      <div className="webinar-header">
        <div className="webinar-title-section">
          <button
            type="button"
            className="btn btn-outline webinar-back-btn"
            onClick={() => {
              saveViewProgress(false);
              navigate('/webinars?section=recordings');
            }}
          >
            <FiArrowLeft /> Назад
          </button>
          <p className="recording-date-label">
            {new Date(webinar.recording_date).toLocaleDateString('ru-RU', { 
              day: 'numeric', 
              month: 'long', 
              year: 'numeric' 
            })}
          </p>
          <h1>{webinar.title}</h1>
        </div>
        {isModeratorOrManager && canShare && canDistributeWebinar && (
          <div className="webinar-actions-header">
            <button className="btn btn-primary" onClick={() => setShowShareModal(true)}>
              <FiShare2 /> Поделиться ссылкой на видео
            </button>
          </div>
        )}
      </div>

      <div className="webinar-content">
        <div className="webinar-main">
          {(isPeertubeInProgress(webinar.peertube_status) || webinar.peertube_status === 'error') && (
            <div className={`peertube-banner peertube-banner--${webinar.peertube_status}`}>
              <div>
                <strong>{peertubeStatusLabel(webinar.peertube_status)}</strong>
                {webinar.peertube_status === 'error' && webinar.peertube_error
                  ? `. ${webinar.peertube_error}`
                  : isPeertubeInProgress(webinar.peertube_status)
                    ? '. Плеер переключится на PeerTube, как только ролик будет готов.'
                    : ''}
              </div>
              {isModerator && webinar.has_video_file && webinar.peertube_status === 'error' && (
                <button type="button" className="btn btn-outline btn-sm" onClick={handleRetryPeertubeUpload}>
                  <FiUpload /> Повторить загрузку
                </button>
              )}
            </div>
          )}
          {(isAiBriefInProgress(webinar.ai_brief_status) || webinar.ai_brief_status === 'error') && (
            <div className={`peertube-banner peertube-banner--${webinar.ai_brief_status === 'error' ? 'error' : 'processing'}`}>
              <div>
                <strong>{aiBriefStatusLabel(webinar.ai_brief_status)}</strong>
                {webinar.ai_brief_status === 'error' && webinar.ai_brief_error
                  ? `. ${webinar.ai_brief_error}`
                  : isAiBriefInProgress(webinar.ai_brief_status)
                    ? '. Краткое содержание, препараты, зоны и тайм-коды появятся сами.'
                    : ''}
              </div>
              {isModerator && webinar.ai_brief_status === 'error' && (
                <button type="button" className="btn btn-outline btn-sm" onClick={handleGenerateAiBrief}>
                  <FiCpu /> Повторить
                </button>
              )}
            </div>
          )}
          <VideoPlayer
            ref={videoPlayerRef}
            onProgress={handleVideoProgress}
            url={(function () {
              if (webinar.external_peertube_url) {
                return (webinar.external_peertube_url || '').trim() || null;
              }
              if (webinar.external_video_url) {
                let raw = (webinar.external_video_url || '').trim();
                if (!raw) return null;
                try {
                  const u = new URL(raw);
                  if (!u.pathname.endsWith('/download')) {
                    if (!u.pathname.endsWith('/')) {
                      u.pathname += '/';
                    }
                    u.pathname += 'download';
                  }
                  return u.toString();
                } catch (e) {
                  if (!raw.endsWith('/download')) {
                    raw = raw.replace(/\/+$/, '') + '/download';
                  }
                  return raw;
                }
              }
              return webinar.video_file;
            })()}
            webinarTitle={webinar?.title}
            webinarDate={webinar?.recording_date}
            speakerName={webinar?.speaker_data?.full_name}
            chronologyStructure={webinar?.chronology_structure}
          />

          {isClient && (
            <div className="client-webinar-meta-row">
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={handleToggleLike}
                disabled={likeLoading}
              >
                <FiHeart /> Лайк ({webinar.likes_count || 0})
              </button>
              <div className="client-webinar-views">
                <FiEye /> {webinar.views_count || 0}
              </div>
            </div>
          )}

          {hasChronology ? (
            <details className="chronology-details-block">
              <summary>Хронология и структура</summary>
              <div className="chronology-lines">
                {chronologyLines.map((line, idx) => {
                  const m = line.match(/\b(\d{2}:\d{2}:\d{2})\b/);
                  const sec = m ? parseHms(m[1]) : null;
                  const displayLine = m ? line.replace(m[1], '').replace(/\s{2,}/g, ' ').trim() : line;
                  return (
                    m && typeof sec === 'number' ? (
                      <button
                        key={`${line}-${idx}`}
                        type="button"
                        className="chronology-line-item chronology-line-item-clickable"
                        onClick={() => seekToSeconds(sec)}
                        title="Перейти к фрагменту"
                      >
                        <span className="summary-time-link">{m[1]}</span>
                        <SimpleMarkdown text={displayLine} />
                      </button>
                    ) : (
                      <div key={`${line}-${idx}`} className="chronology-line-item">
                        <SimpleMarkdown text={displayLine} />
                      </div>
                    )
                  );
                })}
              </div>
            </details>
          ) : (
          /* Временные метки */
          webinar.timestamps && webinar.timestamps.length > 0 && (
            <div className="timestamps-section" style={{ marginTop: '20px', marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '10px', fontSize: '18px' }}>Временные метки</h3>
              <div className="timestamps-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {webinar.timestamps.map((ts) => (
                  <button
                    key={ts.id}
                    onClick={() => {
                      if (videoPlayerRef.current) {
                        try {
                          videoPlayerRef.current.seekTo(ts.time_seconds);
                          // Можно добавить небольшую задержку для плавности
                          setTimeout(() => {
                            // Видео автоматически начнет воспроизведение после перемотки
                          }, 100);
                        } catch (error) {
                          console.error('Error seeking to timestamp:', error);
                        }
                      }
                    }}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: ts.type === 'practice' ? 'var(--timestamp-practice-color, #fff3cd)' : 'var(--timestamp-theory-color, #d1ecf1)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <span style={{ fontWeight: 'bold', color: '#333' }}>{ts.formatted_time}</span>
                    <span style={{ color: '#666' }}>{ts.label}</span>
                    <span style={{
                      fontSize: '12px',
                      padding: '2px 6px',
                      backgroundColor: ts.type === 'practice' ? 'var(--timestamp-practice-badge-color, #ffc107)' : 'var(--timestamp-theory-badge-color, #17a2b8)',
                      color: 'white',
                      borderRadius: '3px'
                    }}>
                      {ts.type === 'practice' ? 'Практика' : 'Теория'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}

          {isClient && webinar.speaker_data && (
            <div className="speaker-card speaker-card-compact">
              <div className="speaker-compact-top">
                {webinar.speaker_data.photo && (
                  <img src={webinar.speaker_data.photo} alt={webinar.speaker_data.full_name} className="speaker-photo" />
                )}
                <div className="speaker-info">
                  <h3>{webinar.speaker_data.full_name}</h3>
                  <p>{webinar.speaker_data.credentials}</p>
                </div>
              </div>
            </div>
          )}

          {isClient && webinar.comments_enabled && (
            <CommentsSection webinarId={id} />
          )}

          {/* Теги под видео + редактирование для модераторов */}
          {(webinar.ai_tags && webinar.ai_tags.length > 0) && (
            <div className="webinar-tags-section">
              <div className="webinar-tags-header">
                <span className="webinar-tags-title">Теги</span>
                {isModeratorOrManager && (
                  !editingTags ? (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm tags-edit-btn"
                      onClick={startEditTags}
                    >
                      <FiEdit /> Редактировать
                    </button>
                  ) : (
                    <div className="tags-edit-actions">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleSaveTags}
                        disabled={savingTags}
                      >
                        {savingTags ? 'Сохранение...' : 'Сохранить'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={cancelEditTags}
                        disabled={savingTags}
                      >
                        Отмена
                      </button>
                    </div>
                  )
                )}
              </div>

              {!editingTags && (
                <div className="tags clickable-tags">
                  {webinar.ai_tags.map((tag, idx) => {
                    const tagText = tag.startsWith('#') ? tag.substring(1) : tag;
                    return (
                      <Link
                        key={idx}
                        to={`/search?q=${encodeURIComponent(tagText)}`}
                        className="tag tag-link"
                      >
                        {tag}
                      </Link>
                    );
                  })}
                </div>
              )}

              {editingTags && (
                <div className="tags-edit">
                  <div className="tags-edit-list">
                    {tagsDraft.length === 0 && (
                      <p className="tags-empty-hint">Тегов пока нет. Добавьте первые хэштеги.</p>
                    )}
                    {tagsDraft.map((tag, idx) => (
                      <div key={idx} className="tag-edit-item">
                        <span>{tag}</span>
                        <button
                          type="button"
                          className="tag-edit-remove-btn"
                          onClick={() => handleDeleteTag(idx)}
                          title="Удалить тег"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="tag-edit-add">
                    <input
                      type="text"
                      className="tag-edit-input"
                      placeholder="#новый_тег"
                      value={newTagValue}
                      onChange={(e) => setNewTagValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={handleAddTag}
                    >
                      <FiPlus /> Добавить
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Транскрипция — раскрывающийся блок в небольшом окошке */}
          {!isClient && (webinar.transcription_with_timestamps?.length > 0 || webinar.transcription) && (
            <div className="transcription-section transcription-collapsible">
              <button
                type="button"
                className="transcription-toggle"
                onClick={() => setTranscriptionExpanded(!transcriptionExpanded)}
                aria-expanded={transcriptionExpanded}
              >
                <h3>Транскрипция</h3>
                {transcriptionExpanded ? (
                  <FiChevronUp className="transcription-chevron" />
                ) : (
                  <FiChevronDown className="transcription-chevron" />
                )}
              </button>
              {transcriptionExpanded && (
                <div className="transcription-window">
                  {webinar.transcription_with_timestamps && webinar.transcription_with_timestamps.length > 0 ? (
                    <div className="transcription-list">
                      {webinar.transcription_with_timestamps.map((segment, idx) => {
                        const speakerColors = {
                          'Спикер 1': '#4a90e2',
                          'Спикер 2': '#e24a4a',
                          'SPEAKER_00': '#4a90e2',
                          'SPEAKER_01': '#e24a4a',
                        };
                        const speakerColor = speakerColors[segment.speaker] || '#666';
                        return (
                          <div
                            key={idx}
                            className="transcription-segment"
                            onClick={() => {
                              if (videoPlayerRef.current && segment.start !== undefined) {
                                try {
                                  videoPlayerRef.current.seekTo(segment.start);
                                } catch (error) {
                                  console.error('Error seeking to timestamp:', error);
                                }
                              }
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="transcription-header">
                              <span className="transcription-time">
                                {segment.start_formatted} - {segment.end_formatted}
                              </span>
                              <span
                                className="transcription-speaker"
                                style={{
                                  backgroundColor: speakerColor + '20',
                                  color: speakerColor,
                                  borderColor: speakerColor
                                }}
                              >
                                {segment.speaker}
                              </span>
                            </div>
                            <div className="transcription-text">{segment.text}</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="transcription-text-simple">
                      {webinar.transcription.split('\n').map((line, idx) => (
                        <p key={idx}>{line}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ИИ Выжимка */}
          {(webinar.ai_topic || webinar.ai_medications?.length > 0 || webinar.ai_body_zones?.length > 0) && (
            <div className="ai-summary">
              <h3>Выжимка от ИИ</h3>
              {webinar.ai_topic && (
                <div className="ai-summary-item">
                  <h4>Общая тема:</h4>
                  <p>{webinar.ai_topic}</p>
                </div>
              )}
              {webinar.ai_medications && webinar.ai_medications.length > 0 && (
                <div className="ai-summary-item">
                  <h4>Препараты:</h4>
                  <div className="tags">
                    {webinar.ai_medications.map((med, idx) => (
                      <span key={idx} className="tag">{med}</span>
                    ))}
                  </div>
                </div>
              )}
              {webinar.ai_body_zones && webinar.ai_body_zones.length > 0 && (
                <div className="ai-summary-item">
                  <h4>Области тела:</h4>
                  <div className="tags">
                    {webinar.ai_body_zones.map((zone, idx) => (
                      <span key={idx} className="tag">{zone}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!isClient && webinar.comments_enabled && (
            <CommentsSection webinarId={id} />
          )}
        </div>

        {!isClient && (
        <div className="webinar-sidebar">
          <div className="stats-card">
            <h3>Статистика</h3>
            <div className="stat-item">
              <span>Просмотры</span>
              <strong>{webinar.views_count}</strong>
            </div>
            <div className="stat-item">
              <span>Лайки</span>
              <strong>{webinar.likes_count}</strong>
            </div>
            <div className="stat-item">
              <span><FiThumbsDown style={{ marginRight: '5px', verticalAlign: 'middle' }} />Дизлайки</span>
              <strong>{webinar.dislikes_count || 0}</strong>
            </div>
            <div className="stat-item">
              <span>Комментарии</span>
              <strong>{webinar.comments_count}</strong>
            </div>
          </div>

          {/* Окно спикера под статистикой */}
          {webinar.speaker_data && (
            <div className="speaker-card sidebar-speaker">
              {webinar.speaker_data.photo && (
                <img
                  src={webinar.speaker_data.photo}
                  alt={webinar.speaker_data.full_name}
                  className="speaker-photo"
                />
              )}
              <div className="speaker-info">
                <h3>{webinar.speaker_data.full_name}</h3>
                <p>{webinar.speaker_data.credentials}</p>
              </div>
            </div>
          )}

          {/* Кнопка Информация */}
          <button className="btn btn-outline info-btn" onClick={() => setShowInfoModal(true)}>
            <FiInfo /> Информация о вебинаре
          </button>

          {isModerator && webinar.has_video_file && !webinar.external_peertube_url && !isPeertubeInProgress(webinar.peertube_status) && (
            <button className="btn btn-outline info-btn" onClick={handleRetryPeertubeUpload}>
              <FiUpload /> Загрузить в PeerTube
            </button>
          )}

          {isModerator && (webinar.external_peertube_url || webinar.peertube_status === 'ready') && !isAiBriefInProgress(webinar.ai_brief_status) && (
            <button className="btn btn-outline info-btn" onClick={handleGenerateAiBrief}>
              <FiCpu /> Заполнить карточку из субтитров PeerTube
            </button>
          )}

          {/* Кнопка Создать транскрипцию (только для модераторов) */}
          {isModerator && (
            <div 
              className="transcription-button-wrapper"
              onMouseEnter={() => {
                if (transcriptionStatus && (transcriptionStatus.state === 'PROGRESS' || transcriptionStatus.state === 'PENDING')) {
                  setShowCancelButton(true);
                }
              }}
              onMouseLeave={() => setShowCancelButton(false)}
            >
              <button 
                className={`btn btn-outline info-btn ${transcriptionStatus && (transcriptionStatus.state === 'PROGRESS' || transcriptionStatus.state === 'PENDING') ? 'transcription-in-progress' : ''}`}
                onClick={handleGenerateTranscription}
                disabled={transcriptionStatus && (transcriptionStatus.state === 'PROGRESS' || transcriptionStatus.state === 'PENDING')}
                style={{ position: 'relative', width: '100%' }}
              >
                <FiFileText /> 
                {transcriptionStatus && (transcriptionStatus.state === 'PROGRESS' || transcriptionStatus.state === 'PENDING') 
                  ? 'Создание транскрипции...' 
                  : 'Создать транскрипцию'}
              </button>
              
              {/* Кнопка отмены (появляется при наведении) */}
              {showCancelButton && transcriptionStatus && (transcriptionStatus.state === 'PROGRESS' || transcriptionStatus.state === 'PENDING') && (
                <button
                  className="btn btn-danger cancel-transcription-btn"
                  onClick={handleCancelTranscription}
                  style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    padding: '8px 12px',
                    fontSize: '12px',
                    zIndex: 10
                  }}
                >
                  Отмена
                </button>
              )}
              
              {/* Прогресс-бар */}
              {transcriptionStatus && transcriptionStatus.state === 'PROGRESS' && (
                <div style={{ marginTop: '10px', width: '100%' }}>
                  <div style={{
                    width: '100%',
                    height: '6px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${transcriptionStatus.progress || 0}%`,
                      height: '100%',
                      backgroundColor: '#ff6600',
                      transition: 'width 0.3s ease',
                      borderRadius: '3px'
                    }} />
                  </div>
                  <div style={{
                    marginTop: '5px',
                    fontSize: '12px',
                    color: '#666',
                    textAlign: 'center'
                  }}>
                    {transcriptionStatus.status || 'В процессе...'} ({transcriptionStatus.progress || 0}%)
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Кнопка Статистика просмотров (только для модераторов) */}
          {isModeratorOrManager && (
            <button className="btn btn-outline info-btn" onClick={() => setShowStatsModal(true)}>
              <FiBarChart2 /> Статистика просмотров
            </button>
          )}
          {isModeratorOrManager && webinar.has_quiz && (
            <button className="btn btn-outline info-btn" onClick={() => setShowQuizStatsModal(true)}>
              <FiCheckSquare /> Статистика ответов
            </button>
          )}
        </div>
        )}
      </div>

      {showInfoModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Информация о вебинаре</h2>
              <button className="modal-close" onClick={() => setShowInfoModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-form">
              <div className="info-section">
                <div className="info-item">
                  <strong>Дата загрузки:</strong>
                  <span>{new Date(webinar.created_at).toLocaleString('ru-RU')}</span>
                </div>
                <div className="info-item">
                  <strong>Дата записи:</strong>
                  <span>{new Date(webinar.recording_date).toLocaleDateString('ru-RU')}</span>
                </div>
                {webinar.speaker_data && (
                  <div className="info-item">
                    <strong>Спикер:</strong>
                    <span>{webinar.speaker_data.full_name}</span>
                  </div>
                )}
                <div className="info-item">
                  <strong>Тема:</strong>
                  <span>{webinar.title}</span>
                </div>
                {webinar.summary && (
                  <div className="info-item full-width">
                    <strong>Краткое содержание:</strong>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{webinar.summary}</p>
                  </div>
                )}
                {webinar.ai_topic && !webinar.summary && (
                  <div className="info-item full-width">
                    <strong>Краткое содержание (ИИ):</strong>
                    <p>{webinar.ai_topic}</p>
                  </div>
                )}
                {webinar.ai_tags && webinar.ai_tags.length > 0 && (
                  <div className="info-item full-width">
                    <strong>Теги:</strong>
                    <div className="tags">
                      {webinar.ai_tags.map((tag, idx) => (
                        <span key={idx} className="tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
                {webinar.is_private && webinar.password && (
                  <div className="info-item">
                    <strong>Пароль для доступа:</strong>
                    <span>{webinar.password}</span>
                  </div>
                )}
                {webinar.created_by_data && (
                  <div className="info-item">
                    <strong>Загрузил:</strong>
                    <span>{webinar.created_by_data.email}</span>
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button className="btn btn-primary" onClick={() => setShowInfoModal(false)}>
                  Закрыть
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для управления ссылками */}
      {showShareModal && (
        <ShareModal
          open={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            fetchWebinar();
          }}
          webinar={webinar}
        />
      )}

      {/* Модальное окно статистики просмотров */}
      {showStatsModal && (
        <WebinarStatsModal
          open={showStatsModal}
          onClose={() => setShowStatsModal(false)}
          webinarId={id}
        />
      )}

      {showQuizStatsModal && (
        <QuizStatsModal
          open={showQuizStatsModal}
          onClose={() => setShowQuizStatsModal(false)}
          webinarId={id}
        />
      )}
    </div>
  );
};

export default WebinarDetail;
